/**
 * IrrigationPlanConfig component
 * Displays the configuration tab with form and action buttons
 */

import { useState } from "react";
import type { AUTProject } from "@/api/queries/account";
import ConfirmModal from "@/components/ConfirmModal";
import Button from "@/components/ui/Button";
import IrrigationPlanConfigPanel from "@/pages/main/components/IrrigationPlanConfigPanel";
import type { IrrigationPlanFormData } from "@/pages/main/IrrigationPlanWizardPage";
import { getDisabledReason } from "../utils/validation";

// Use any to avoid type mismatches with AUT types
type Project = AUTProject;

interface IrrigationPlanConfigProps {
  /** Current form data */
  data: IrrigationPlanFormData;
  /** The project this plan belongs to */
  project?: Project;
  /** Handler for data changes */
  onChange: (updates: Partial<IrrigationPlanFormData>) => void;
  /** Validation message for start time conflicts */
  startValidationMessage: string | null;
  /** Optional hint that disabling resumption would resolve the conflict */
  resumptionHint: string | null;
  /** Minimum gap between plans in minutes */
  minimumGapMinutes: number;
  /** Whether the form is read-only */
  readOnly: boolean;
  /** Whether the form can be saved */
  canSave: boolean;
  /** Handler for save action */
  onSave: () => void;
  /** Handler for cancel action */
  onCancel: () => void;
  /** Handler for delete action */
  onDelete?: () => void;
  /** Whether there are unsaved changes */
  hasChanges: boolean;
  /** Error message to display */
  error?: string | null;
  /** Whether any project is running */
  anyProjectRunning?: boolean;
  /** Whether start validation passed */
  startValidationValid?: boolean;
}

export function IrrigationPlanConfig({
  data,
  project,
  onChange,
  startValidationMessage,
  resumptionHint,
  minimumGapMinutes,
  readOnly,
  canSave,
  onSave,
  onCancel,
  onDelete,
  hasChanges,
  error,
  anyProjectRunning = false,
  startValidationValid = true,
}: IrrigationPlanConfigProps) {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Get user-friendly reason for why save is disabled
  const disabledReason = getDisabledReason(data, canSave, startValidationValid, anyProjectRunning);

  return (
    <>
      <IrrigationPlanConfigPanel
        data={data}
        project={project}
        onChange={onChange}
        startValidationMessage={startValidationMessage}
        resumptionHint={resumptionHint}
        minimumGapMinutes={minimumGapMinutes}
        readOnly={readOnly}
      />
      {error && (
        <div className="px-4">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">{error}</div>
        </div>
      )}
      <div className="flex items-center justify-between mt-3 pt-3 px-4 border-t border-gray-200">
        <div>
          {onDelete && (
            <Button
              onClick={() => setIsDeleteModalOpen(true)}
              variant="destructive"
              size="md"
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Apagar
            </Button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={onCancel}
            variant="secondary"
            size="md"
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancelar
          </Button>
          <Button
            onClick={onSave}
            variant="primary"
            size="md"
            disabled={!canSave}
            title={!canSave ? disabledReason : undefined}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Salvar
          </Button>
        </div>
      </div>
      {onDelete && (
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={onDelete}
          title="Excluir Plano de Irrigação"
          message="Tem certeza que deseja apagar este plano? Os dados do histórico serão perdidos e esta ação não poderá ser desfeita."
          confirmText="Apagar"
          cancelText="Cancelar"
          variant="danger"
        />
      )}
    </>
  );
}
